import type { Transport } from './transport';

/** How far ahead sounds are scheduled, and how often the scheduler wakes up. */
const LOOKAHEAD = 0.2;
const INTERVAL_MS = 25;

export interface TimedEvent {
  /** Song position, seconds. */
  time: number;
}

interface IntervalApi {
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
}

const defaultIntervals: IntervalApi = {
  setInterval: (fn, ms) => globalThis.setInterval(fn, ms),
  clearInterval: (h) => {
    globalThis.clearInterval(h as ReturnType<typeof setInterval>);
  },
};

/**
 * Plays sounds at song positions in time with the transport (metronome, chord synth).
 * A look-ahead scheduler: a timer wakes every 25 ms and schedules, at exact context times, the
 * events due in the next 200 ms. Positions map to context times through the transport's own
 * schedule, so the sounds stay sample-aligned with the music at any speed and across loops.
 * When the schedule changes (seek, pause, speed), sounds not yet heard are stopped and
 * rescheduled.
 */
export class SoundScheduler<E extends TimedEvent> {
  private events: E[] = [];
  /** Unwrapped song position up to which events are scheduled. */
  private scheduledTo = -Infinity;
  private pending: { when: number; nodes: AudioScheduledSourceNode[] }[] = [];
  private timer: unknown = null;
  private readonly unsubscribe: () => void;

  constructor(
    private readonly clock: { readonly currentTime: number },
    private readonly transport: Transport,
    private readonly play: (event: E, when: number, next: number) => AudioScheduledSourceNode[],
    private readonly intervals: IntervalApi = defaultIntervals,
  ) {
    this.unsubscribe = transport.on('change', () => {
      this.reset();
    });
  }

  /** Events sorted by time. Replacing them reschedules sounds not yet heard. */
  setEvents(events: E[]): void {
    this.events = events;
    this.reset();
  }

  start(): void {
    if (this.timer !== null) return;
    this.reset();
    this.timer = this.intervals.setInterval(() => {
      this.tick();
    }, INTERVAL_MS);
  }

  stop(): void {
    if (this.timer !== null) this.intervals.clearInterval(this.timer);
    this.timer = null;
    this.cancelFrom(this.clock.currentTime);
  }

  dispose(): void {
    this.stop();
    this.unsubscribe();
  }

  /** Called on every transport change: drop what hasn't sounded, schedule from the new anchor. */
  private reset(): void {
    const { output, input } = this.transport.anchor;
    this.cancelFrom(Math.max(output, this.clock.currentTime));
    // Events exactly at the anchor (a seek to a beat) still play.
    this.scheduledTo = input - 1e-4;
    if (this.timer !== null) this.tick();
  }

  private cancelFrom(time: number): void {
    this.pending = this.pending.filter((p) => {
      if (p.when < time) return true;
      for (const node of p.nodes) {
        try {
          node.stop();
        } catch {
          // Already stopped.
        }
      }
      return false;
    });
  }

  tick(): void {
    const now = this.clock.currentTime;
    this.pending = this.pending.filter((p) => p.when > now - 3);
    const horizon = this.transport.unwrappedAt(now + LOOKAHEAD);
    if (horizon === null || horizon <= this.scheduledTo || !this.events.length) return;
    const from = this.scheduledTo;
    this.scheduledTo = horizon;

    const loop = this.transport.loop;
    const schedule = (event: E, unwrapped: number, nextUnwrapped: number) => {
      const when = this.transport.outputTimeOf(unwrapped);
      if (when < now - 0.005) return;
      const next = this.transport.outputTimeOf(nextUnwrapped);
      this.pending.push({ when, nodes: this.play(event, when, next) });
    };

    if (!loop) {
      for (let i = this.firstAfter(from); i < this.events.length; i++) {
        const e = this.events[i];
        if (!e || e.time > horizon) break;
        if (e.time > this.transport.duration) break;
        schedule(e, e.time, this.events[i + 1]?.time ?? this.transport.duration);
      }
      return;
    }

    // Inside a loop, position (unwrapped) u corresponds to loop.start + ((u − start) mod length).
    const length = loop.end - loop.start;
    const firstCycle = Math.floor((Math.max(from, loop.start) - loop.start) / length);
    const lastCycle = Math.floor((horizon - loop.start) / length);
    for (let c = firstCycle; c <= lastCycle; c++) {
      const offset = c * length;
      for (let i = this.firstAfter(loop.start - 1e-9); i < this.events.length; i++) {
        const e = this.events[i];
        if (!e || e.time >= loop.end) break;
        const u = e.time + offset;
        if (u <= from) continue;
        if (u > horizon) break;
        const nextTime = Math.min(this.events[i + 1]?.time ?? loop.end, loop.end);
        schedule(e, u, nextTime + offset);
      }
    }
  }

  /** Index of the first event after song position `t`. */
  private firstAfter(t: number): number {
    let lo = 0;
    let hi = this.events.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((this.events[mid]?.time ?? Infinity) <= t) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }
}
